import { useEffect, useState, type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { refreshAccessToken } from '@/lib/api/client'
import { getMe } from '@/lib/api/auth'
import { FullPageLoading } from '@/design'

/**
 * Resolves the session on boot. Because the access token is memory-only (A2), a page reload always
 * exchanges the persisted refresh token for a fresh access token, then loads the authoritative
 * user via GET /auth/me (never trusts a cached role).
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status)
  const logoutReason = useAuthStore((s) => s.logoutReason)
  const [resolving, setResolving] = useState(status !== 'authenticated')

  useEffect(() => {
    let active = true
    const store = useAuthStore.getState()

    async function resolve() {
      if (store.status === 'authenticated') {
        setResolving(false)
        return
      }
      if (!store.getRefreshToken()) {
        store.setStatus('anonymous')
        setResolving(false)
        return
      }
      store.setStatus('authenticating')
      try {
        await refreshAccessToken()
        const me = await getMe()
        if (active) store.setUser(me)
      } catch {
        if (active) store.forceLogout()
      } finally {
        if (active) setResolving(false)
      }
    }

    void resolve()
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (resolving || status === 'unknown' || status === 'authenticating') {
    return <FullPageLoading />
  }
  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ reason: logoutReason }} />
  }
  return <>{children}</>
}
